import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { compareSync } from 'bcryptjs';
import { View } from 'react-native';
import { hasInternetAsync } from '@/screens/HomeTherapist/helpers';
import { useFocusEffect } from '@react-navigation/native';
import { Text } from '@/components/Text';
import { TherapistStackParamList } from '@/navigations/TherapistStack';
import { StackScreenProps } from '@react-navigation/stack';
import { useNotification } from '@/components/ui/therapist/Notification/NotificationProvider';
import { useSidebarAnimation } from '@/components/ui/therapist/CollapsibleSidebar/SidebarAnimationContext';

import PasswordGesture from '@/components/ReactNativeGesturePassword';
import { saveNewPatternLock } from '@/api/services/user';
import store, { useStoreState } from '@/stores/store';
import { PressableNeolexon } from '@/components/PressableNeolexon';
import { styles } from './styles';
import { logError } from '@/utils/logHandling';

const MIN_PASSWORD_LENGTH = 4;
const MESSAGES = {
  SET_PATTERN: 'Legen Sie ein Sperrmuster fest.',
  REPEAT_PATTERN: 'Wiederholen Sie das Sperrmuster.',
  ENTER_CURRENT: 'Aktuelles Sperrmuster eingeben',
  MIN_POINTS_ERROR: 'Verbinden Sie mindestens 4 Punkte.',
  MISMATCH_ERROR: 'Sperrmuster Wiederholung stimmt nicht überein.',
  SETTING_PATTERN: 'Sperrmuster wird gesetzt...',
  SUCCESS_NOTIFICATION: 'Sperrmuster geändert.',
  CANCEL: 'Abbrechen',
};

type PasswordStep = 'enter-current' | 'set-new' | 'confirm-new';
type PasswordStatus = 'normal' | 'wrong' | 'right';

export const SetPassword: FC<
  StackScreenProps<TherapistStackParamList, 'SetPassword'>
> = ({ navigation, route }) => {
  const [currentStep, setCurrentStep] = useState<PasswordStep>('set-new');
  const [newPassword, setNewPassword] = useState('');
  const [state, setState] = useState<{
    message: string;
    status: PasswordStatus;
  }>({
    message: MESSAGES.SET_PATTERN,
    status: 'normal',
  });

  const gestureRef = useRef<PasswordGesture | null>(null);

  const { setShowHeader } = useSidebarAnimation();
  const { showNotification } = useNotification();

  const { PASSWORD } = useStoreState((store) => ({
    PASSWORD: store.appLockPattern,
  }));

  const isEditMode = !!PASSWORD && route.params?.mode !== 'set-new';

  const handleCurrentPasswordValidation = (password: string) => {
    if (compareSync(password, PASSWORD)) {
      setCurrentStep('set-new');
      setState({
        status: 'normal',
        message: MESSAGES.SET_PATTERN,
      });
    } else {
      gestureRef.current?.resetPasswordGesture();
      showNotification({
        type: 'error',
        title: 'Sperrmuster ist falsch',
      });
      navigation.navigate('PasswordUnlock', { fromRoute: 'SetPassword' });
    }
  };

  const handleNewPasswordEntry = (password: string) => {
    if (password.split(',').length < MIN_PASSWORD_LENGTH) {
      setState({
        status: 'wrong',
        message: MESSAGES.MIN_POINTS_ERROR,
      });
      return;
    }

    setNewPassword(password);
    setCurrentStep('confirm-new');
    setState({
      status: 'normal',
      message: MESSAGES.REPEAT_PATTERN,
    });
  };

  const handlePasswordConfirmation = async (password: string) => {
    if (password !== newPassword) {
      setState({
        status: 'wrong',
        message: MESSAGES.MISMATCH_ERROR,
      });
      return;
    }

    try {
      setState({
        status: 'right',
        message: MESSAGES.SETTING_PATTERN,
      });

      const { hash, status } = await saveNewPatternLock(password);

      if (status === 200) {
        store.getActions().setAppLockPattern(hash);
        showNotification({
          type: 'success',
          title: MESSAGES.SUCCESS_NOTIFICATION,
        });
      }

      resetAndNavigate();
    } catch (error) {
      logError('Could not save new lock pattern', error);
      resetAndNavigate();
    }
  };

  const resetAndNavigate = () => {
    setNewPassword('');
    setCurrentStep('set-new');
    navigation.navigate('LockScreen');
  };

  const onEnd = async (password: string) => {
    const hasInternet = await hasInternetAsync();

    if (hasInternet === false) {
      showNotification({
        type: 'error',
        title: 'Keine Verbindung',
        message:
          'Keine Verbindung zum neolexon Server. Überprüfen Sie Ihre Internetverbindung und versuchen es erneut.',
      });
      return;
    }

    switch (currentStep) {
      case 'enter-current':
        handleCurrentPasswordValidation(password);
        break;
      case 'set-new':
        handleNewPasswordEntry(password);
        break;
      case 'confirm-new':
        handlePasswordConfirmation(password);
        break;
    }
  };

  const goBack = () => {
    setShowHeader(true);
    setNewPassword('');
    setCurrentStep('set-new');
    setState({
      message: MESSAGES.SET_PATTERN,
      status: 'normal',
    });
    navigation.goBack();
  };

  useEffect(() => {
    if (isEditMode) {
      setCurrentStep('enter-current');
      setState({
        status: 'normal',
        message: MESSAGES.ENTER_CURRENT,
      });
    } else {
      setCurrentStep('set-new');
      setState({
        status: 'normal',
        message: MESSAGES.SET_PATTERN,
      });
    }
  }, [isEditMode]);

  useFocusEffect(
    useCallback(() => {
      setShowHeader(false);

      return () => {
        setShowHeader(true);
      };
    }, []),
  );

  return (
    <View style={styles.setPasswordContainer}>
      <View style={{ maxHeight: 360, flex: 1 }}>
        <PasswordGesture
          ref={gestureRef}
          status={state.status}
          message={state.message}
          onEnd={onEnd}
          innerCircle={true}
          outerCircle={true}
          showNotification={showNotification}
        />
        <View style={styles.setPasswordBtnContainer}>
          <PressableNeolexon onPress={() => goBack()} style={styles.btn}>
            <Text style={styles.setPasswordText}>Abbrechen</Text>
          </PressableNeolexon>
        </View>
      </View>
      {isEditMode ? (
        <View style={styles.btnContainer}>
          <PressableNeolexon
            onPress={() => {
              navigation.navigate('PasswordUnlock', {
                fromRoute: 'SetPassword',
              });
            }}
            style={styles.flexOne}
          >
            <Text style={styles.text}>Sperrmuster vergessen?</Text>
          </PressableNeolexon>
        </View>
      ) : null}
    </View>
  );
};
